import { useState } from 'react'
import supabase from '../supabase-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import './Register.css'

export default function Register({ onRegister, onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      return false
    }
    if (!formData.fullName.trim()) {
      setError('Full name is required')
      return false
    }
    return true
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          }
        }
      })

      if (error) {
        setError(error.message)
      } else {
        if (data.user && !data.user.email_confirmed_at) {
          setSuccess('Registration successful! Please check your email and click the confirmation link. After confirming, you can sign in with your credentials.')
        } else {
          setSuccess('Registration successful!')
          setTimeout(() => {
            onRegister(data.user)
          }, 1500)
        }
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <div className="logo">
            <img src="/odinlogo.png" alt="Odin Logo" className="w-32 h-32 object-contain" />
          </div>
          <h2 className="register-title">Join Odin</h2>
          <p className="register-subtitle">Create your Mushroom Farm Management account</p>
        </div>
        
        <form className="register-form" onSubmit={handleRegister}>
          <div className="form-group">
            <label htmlFor="fullName" className="sr-only">
              Full Name
            </label>
            <Input
              id="fullName"
              name="fullName"
              type="text"
              required
              className="form-input name-input"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email-address" className="sr-only">
              Email address
            </label>
            <Input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="form-input email-input"
              placeholder="Email address"
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="form-group password-group">
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className="form-input password-input"
              placeholder="Password (min. 6 characters)"
              value={formData.password}
              onChange={handleInputChange}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </Button>
          </div>

          <div className="form-group password-group">
            <label htmlFor="confirmPassword" className="sr-only">
              Confirm Password
            </label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className="form-input confirm-password-input"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </Button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
              {success.includes('Please check your email') && (
                <div className="back-to-login">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onSwitchToLogin}
                  >
                    Back to Sign In
                  </Button>
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <div className="loading-content">
                <div className="spinner"></div>
                Creating account...
              </div>
            ) : (
              'Create Account'
            )}
          </Button>

          <div className="register-footer">
            <p>
              Already have an account?{' '}
              <Button
                type="button"
                variant="link"
                onClick={onSwitchToLogin}
              >
                Sign in here
              </Button>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
